import DashboardLayout from "@/components/DashboardLayout";
import { PurchaseHistoryTab } from "@/components/PurchaseHistoryTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { mapCustomerUpdateErrorMessage } from "@/lib/customerFormErrors";
import { copyTextToClipboard } from "@/lib/copyTextToClipboard";
import RegenerateProposalButton from "@/components/sales/RegenerateProposalButton";
import { AddressSearchInput } from "@/components/AddressSearchInput";
import { CustomerCardTimeline } from "@/components/CustomerCardTimeline";
import { CleoCustomerCardPanel } from "@/components/CleoCustomerCardPanel";
import CustomerProjectsSection from "@/components/projects/CustomerProjectsSection";
import { CustomerMediaSection } from "@/components/customers/CustomerMediaSection";
import { ClarityOnePortalToggle } from "@/components/clarity-one/ClarityOnePortalToggle";
import { PaymentWelcomeStatus } from "@/components/clarity-one/PaymentWelcomeStatus";
import { warrantyCardStaffStatusLabel } from "@shared/warrantyDomain";
import { KaleidescapeWalletStaffPanel } from "@/components/clarity-one/KaleidescapeWalletStaffPanel";
import { canStaffImpersonateClarityOneCustomer } from "@shared/clarityOneStaffAccess";
import {
  canCreateProposalRevision,
  shouldUseVariationOrderWorkflow,
} from "@shared/commercialHead";
import { useAuth } from "@/_core/hooks/useAuth";
import { formatDate } from "@/lib/dateUtils";
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  Edit2,
  FileText,
  Mail,
  MapPin,
  Package,
  Pencil,
  Phone,
  Plus,
  Receipt,
  Shield,
  Star,
  Trash2,
  User,
  UserCheck,
  FolderOpen,
  Wrench,
} from "lucide-react";
import CustomerBillingGroupPanel from "@/components/proposals/CustomerBillingGroupPanel";
import { useEffect, useState } from "react";
import { useLocation, useParams, useSearch } from "wouter";
import { usePathname, navigate } from "wouter/use-browser-location";
import { toast } from "sonner";
import { CustomerServiceSitesPanel } from "@/components/customers/CustomerServiceSitesPanel";
import { CustomerDeliveredItemsSection } from "@/components/customers/CustomerDeliveredItemsSection";
import { CustomerPhoneNumbersSection } from "@/components/customers/CustomerPhoneNumbersSection";
import { formatCustomerCreationSource } from "@shared/customerCreationSource";
import { canScheduleServiceVisit } from "@shared/serviceVisitCustomerScheduling";
import {
  customerCardVisibleTabs,
  staffCustomerReadOnlyBanner,
  type CustomerCardTab,
} from "@shared/staffCustomerAccess";
import { canViewWarranty as canViewWarrantyRole } from "@shared/warrantyDomain";
import { normalizeUserRole } from "@shared/userRoles";

/** Create/continue VO — same `variationOrders.canCreate` authority as Deal Overview. */
function CustomerDetailCreateVoButton({
  proposalId,
  fromPath,
}: {
  proposalId: number;
  fromPath: string;
}) {
  const canCreateQ = trpc.variationOrders.canCreate.useQuery(
    { proposalId },
    { enabled: Number.isFinite(proposalId) && proposalId > 0 },
  );
  const activeVo = canCreateQ.data?.activeVo ?? null;
  const createVoBlockedMessage = canCreateQ.data?.blockReason ?? null;
  const eligible = Boolean(canCreateQ.data?.eligible);
  const isDraftActive = activeVo && String(activeVo.status).toLowerCase() === "draft";

  const goToCreateVo = () => {
    if (createVoBlockedMessage) {
      toast.error(createVoBlockedMessage);
      if (activeVo?.id) {
        navigate(`/sales/vo/${activeVo.id}/preview`, { state: { from: fromPath } });
      }
      return;
    }
    if (isDraftActive && activeVo?.id) {
      navigate(`/sales/proposals/${proposalId}/vo/${activeVo.id}`, {
        state: { from: fromPath },
      });
      toast.message(`${activeVo.voNumber} reopened`);
      return;
    }
    navigate(`/sales/proposals/${proposalId}/vo/new`, { state: { from: fromPath } });
  };

  if (canCreateQ.isLoading) {
    return (
      <Button size="sm" variant="default" className="h-8 bg-[#1a2744] text-white" disabled>
        Checking…
      </Button>
    );
  }

  if (!eligible) {
    return null;
  }

  return (
    <Button
      size="sm"
      variant="default"
      className="h-8 bg-[#1a2744] text-white hover:bg-[#1a2744]/90"
      onClick={goToCreateVo}
      disabled={Boolean(createVoBlockedMessage)}
      title={createVoBlockedMessage ?? undefined}
      data-testid={`customer-create-vo-${proposalId}`}
    >
      {isDraftActive ? `Continue ${activeVo.voNumber}` : "Create Variation Order"}
    </Button>
  );
}

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const pathname = usePathname();
  const { user } = useAuth();
  const search = useSearch();
  const customerIdFromRoute = Number.parseInt(id ?? "", 10);
  const hasValidRouteId = Number.isFinite(customerIdFromRoute) && customerIdFromRoute > 0;
  const userRole = normalizeUserRole(user?.role);
  const isAdmin = userRole === "admin";

  const [editingSalesperson, setEditingSalesperson] = useState(false);
  const [selectedSalespersonId, setSelectedSalespersonId] = useState<number | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<number | null>(null);
  const [expandedInternalIds, setExpandedInternalIds] = useState<Set<number>>(new Set());

  // Edit customer state
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [editName, setEditName] = useState("");
  const [editContact, setEditContact] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editUnitNumber, setEditUnitNumber] = useState("");
  const [editPostalCode, setEditPostalCode] = useState("");
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderDate, setReminderDate] = useState("");
  const [reminderTime, setReminderTime] = useState("");
  const [reminderNote, setReminderNote] = useState("");

  const toggleJob = (id: number) => setExpandedJobId((prev) => (prev === id ? null : id));
  const toggleInternal = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedInternalIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const { data, isLoading, error, refetch } = trpc.customers.getById.useQuery(
    { id: customerIdFromRoute },
    { enabled: hasValidRouteId }
  );
  const access = data?.access;
  const canManageCustomer = Boolean(access?.canManageCustomer);
  const canReadCommercial = Boolean(access?.canReadCommercialSalesWork);
  const canReadFinance = Boolean(access?.canReadFinance);
  const canMutatePicOwnedFollowUp = !Boolean(access?.isReadOnlyBecauseOtherPic);
  const canCreateSvr = Boolean(user && canScheduleServiceVisit(user.role));
  const visibleCardTabs = customerCardVisibleTabs(access);
  const canAccessInstallProjects = visibleCardTabs.includes("projects");
  const { data: salesStaff } = trpc.customers.listSalesStaff.useQuery(undefined, { enabled: isAdmin });
  const { data: outstandingInvoices = [] } = trpc.customers.getOutstandingInvoices.useQuery(
    { customerId: customerIdFromRoute },
    { enabled: hasValidRouteId && canReadFinance }
  );
  const { data: customerEmailsData } = trpc.customers.listEmailAddresses.useQuery(
    { customerId: customerIdFromRoute },
    { enabled: hasValidRouteId && Boolean(access?.canReadCustomer) },
  );
  useEffect(() => {
    console.info("[routing-check] entity=customer routeId=%s parsedId=%s valid=%s", id, customerIdFromRoute, hasValidRouteId);
  }, [id, customerIdFromRoute, hasValidRouteId]);
  useEffect(() => {
    if (data?.customer?.id) {
      console.info("[routing-check] entity=customer id=%s fetch=success", data.customer.id);
    } else if (error) {
      console.warn("[routing-check] entity=customer id=%s fetch=failure", customerIdFromRoute, error.message);
    }
  }, [data?.customer?.id, error, customerIdFromRoute]);
  const [activeTab, setActiveTab] = useState<CustomerCardTab>("purchase");
  const [proposalConfirmationTab, setProposalConfirmationTab] = useState<"confirmed" | "unconfirmed">("confirmed");
  const utils = trpc.useUtils();
  const billingGroupsQ = trpc.proposals.listCustomerBillingGroups.useQuery(
    { customerId: customerIdFromRoute },
    { enabled: hasValidRouteId && canReadFinance },
  );
  const proposalCards = trpc.proposals.listCustomerProposalCards.useQuery(
    { customerId: customerIdFromRoute },
    { enabled: hasValidRouteId && canReadCommercial }
  );
  const confirmedProposalCards = (proposalCards.data ?? []).filter(
    (proposal) => proposal.confirmationGroup === "confirmed",
  );
  const unconfirmedProposalCards = (proposalCards.data ?? []).filter(
    (proposal) => proposal.confirmationGroup === "unconfirmed",
  );
  const visibleProposalCards =
    proposalConfirmationTab === "confirmed" ? confirmedProposalCards : unconfirmedProposalCards;
  const paidRetailPurchases = trpc.proposals.listCustomerPaidRetailPurchases.useQuery(
    { customerId: customerIdFromRoute },
    { enabled: hasValidRouteId && canReadCommercial }
  );
  const legacyInvoices = trpc.proposals.listCustomerLegacyInvoices.useQuery(
    { customerId: customerIdFromRoute },
    { enabled: hasValidRouteId && canReadFinance }
  );
  const portalInvoices = trpc.financeInvoices.listCustomerPortalInvoices.useQuery(
    { customerId: customerIdFromRoute },
    { enabled: hasValidRouteId && canReadFinance }
  );
  const portalInvoiceIds = (portalInvoices.data ?? []).map((invoice) => invoice.id);
  const canEditWarranty = userRole === "sales" || userRole === "admin";
  const canViewWarranty = canViewWarrantyRole(user?.role);
  const customerWarranties = trpc.warranties.listForCustomer.useQuery(
    { customerId: customerIdFromRoute },
    { enabled: hasValidRouteId && canViewWarranty },
  );
  const portalWarrantyActions = trpc.warranties.getActionsForPortalInvoices.useQuery(
    { invoiceIds: portalInvoiceIds },
    { enabled: canViewWarranty && portalInvoiceIds.length > 0 },
  );
  const createPortalWarranty = trpc.warranties.createDraft.useMutation({
    onError: (mutationError) =>
      toast.error(mutationError.message || "Could not create warranty draft"),
  });
  const openManualProposalPdf = trpc.proposals.getProposalOriginalPdfUrl.useMutation({
    onSuccess: (res) => {
      window.open(res.url, "_blank", "noopener,noreferrer");
    },
    onError: (e) => toast.error(e.message || "Could not open proposal PDF."),
  });
  const salesTimeline = trpc.proposals.getCustomerSalesTimeline.useQuery(
    { customerId: customerIdFromRoute },
    { enabled: hasValidRouteId && canReadCommercial }
  );
  const reminderRows = trpc.proposals.listCustomerReminders.useQuery(
    { customerId: customerIdFromRoute },
    { enabled: hasValidRouteId && canReadCommercial }
  );
  const setCustomerReminder = trpc.proposals.setCustomerReminder.useMutation({
    onSuccess: async () => {
      toast.success("Reminder set");
      setReminderOpen(false);
      setReminderDate("");
      setReminderTime("");
      setReminderNote("");
      await Promise.all([
        reminderRows.refetch(),
        salesTimeline.refetch(),
      ]);
    },
    onError: (e) => toast.error(e.message || "Could not set reminder"),
  });
  const updateReminderStatus = trpc.proposals.updateCustomerReminderStatus.useMutation({
    onSuccess: async () => {
      await Promise.all([reminderRows.refetch(), salesTimeline.refetch()]);
    },
    onError: (e) => toast.error(e.message || "Could not update reminder"),
  });
  const salesCardMetrics = trpc.customers.getSalesCardMetrics.useQuery(
    { customerId: customerIdFromRoute },
    { enabled: hasValidRouteId && canReadCommercial }
  );
  const customerLogs = trpc.customerLogs.listByCustomer.useQuery(
    { customerId: customerIdFromRoute },
    { enabled: hasValidRouteId && canReadCommercial }
  );

  // Visits = linked, non-deleted sales appointments (derived; no separate visit rows).
  const customerVisits = trpc.salesAppointments.customerVisits.useQuery(
    { customerId: customerIdFromRoute },
    { enabled: hasValidRouteId && canReadCommercial }
  );

  const visibleCardTabsKey = visibleCardTabs.join("|");
  useEffect(() => {
    const t = new URLSearchParams(search).get("tab") as CustomerCardTab | null;
    if (t && visibleCardTabs.includes(t)) {
      setActiveTab(t);
    }
  }, [search, visibleCardTabsKey]);

  useEffect(() => {
    const t = new URLSearchParams(search).get("tab") as CustomerCardTab | null;
    if (t && visibleCardTabs.includes(t)) {
      return;
    }
    if (visibleCardTabs.includes("timeline")) setActiveTab("timeline");
    else if (visibleCardTabs.includes("purchase")) setActiveTab("purchase");
    else setActiveTab("svr");
  }, [customerIdFromRoute, visibleCardTabsKey]);
  const setTestCustomer = trpc.adminCustomers.setTestCustomer.useMutation({
    onSuccess: (res) => {
      toast.success(res.isTest ? "Marked as test customer" : "Test-customer flag removed");
      void refetch();
    },
    onError: (err) => toast.error(err.message || "Could not update test-customer flag"),
  });
  const requestAccess = trpc.customers.requestAccess.useMutation({
    onSuccess: () => toast.success("Access request sent to assigned salesperson and admin."),
    onError: (err) => toast.error(err.message || "Failed to request access."),
  });

  const updateSalesperson = trpc.customers.update.useMutation({
    onSuccess: () => {
      toast.success("Salesperson updated");
      setEditingSalesperson(false);
      refetch();
    },
    onError: (err) =>
      toast.error(mapCustomerUpdateErrorMessage(err.message || "Failed to update salesperson")),
  });

  const updateCustomer = trpc.customers.update.useMutation({
    onSuccess: () => {
      toast.success("Customer updated");
      setEditingCustomer(false);
      refetch();
    },
    onError: (err) =>
      toast.error(mapCustomerUpdateErrorMessage(err.message || "Failed to update customer")),
  });

  const claimCustomer = trpc.customers.claimCustomer.useMutation({
    onSuccess: () => {
      toast.success("Customer claimed — you are now the PIC.");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteCustomer = trpc.admin.deleteCustomer.useMutation({
    onSuccess: () => {
      toast.success("Customer deleted");
      setLocation("/customers");
    },
    onError: (err) => toast.error(err.message || "Failed to delete customer"),
  });

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 border-2 border-gray-300 border-t-black rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!hasValidRouteId) {
    return (
      <DashboardLayout>
        <div className="text-center py-16 text-muted-foreground">Invalid customer ID</div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="text-center py-16 text-muted-foreground">
          Unable to load customer details.
          <div className="text-xs mt-2">{error.message}</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="text-center py-16 text-muted-foreground">Customer not found</div>
      </DashboardLayout>
    );
  }

  const { customer, createdByUserName, creationSource } = data;
  const isTestCustomer = Boolean((customer as { isTest?: unknown }).isTest);
  const jobs = data.jobs ?? [];
  const readOnlyBanner = access ? staffCustomerReadOnlyBanner(access) : null;

  const startEditCustomer = () => {
    setEditName(customer.name);
    setEditContact(customer.contact || "");
    setEditEmail(customer.email || "");
    setEditAddress(customer.address || "");
    setEditUnitNumber((customer as { unitNumber?: string | null }).unitNumber || "");
    setEditPostalCode((customer as { postalCode?: string | null }).postalCode || "");
    setEditingCustomer(true);
  };

  const handleSaveCustomer = () => {
    updateCustomer.mutate({
      id: customer.id,
      name: editName || undefined,
      contact: editContact || undefined,
      email: editEmail || undefined,
      address: editAddress || undefined,
      unitNumber: editUnitNumber || undefined,
      postalCode: editPostalCode || undefined,
    });
  };

  const handleDeleteCustomer = () => {
    if (
      confirm(
        `Delete customer "${customer.name}"? This will not delete their repair orders or products, but cannot be undone.`
      )
    ) {
      deleteCustomer.mutate({ id: customer.id });
    }
  };

  const handleSalespersonSave = () => {
    updateSalesperson.mutate({
      id: customer.id,
      salespersonId: selectedSalespersonId,
    });
  };

  const nameParts = customer.name.trim().split(/\s+/).filter(Boolean);
  const nameInitials =
    nameParts.length === 0 ? "?" : nameParts.length === 1
      ? nameParts[0]!.slice(0, 2).toUpperCase()
      : (nameParts[0]![0]! + nameParts[nameParts.length - 1]![0]!).toUpperCase();
  const firstName = nameParts[0] ?? "Customer";
  const contactDigits = (customer.contact ?? "").replace(/\D/g, "");
  const waHref = contactDigits
    ? contactDigits.length === 8
      ? `https://wa.me/65${contactDigits}`
      : `https://wa.me/${contactDigits}`
    : null;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Outstanding Invoice Banner */}
        {outstandingInvoices.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <span className="text-red-600 mt-0.5">⚠️</span>
            <div className="flex-1">
              <p className="font-semibold text-red-800">Outstanding Invoice{outstandingInvoices.length > 1 ? 's' : ''}</p>
              <div className="mt-1 space-y-1">
                {outstandingInvoices.map((inv: { id: number; segment?: string; invoiceNumber?: string | null; visitNumber?: string | null; rate: unknown; invoiceStatus: string; invoiceToken?: string | null }) => (
                  <p key={inv.segment === "PORT" ? `port-${inv.id}` : `svr-${inv.id}`} className="text-sm text-red-700">
                    {inv.invoiceNumber ?? inv.visitNumber} — SGD {inv.rate != null ? Number(inv.rate as number).toFixed(2) : '—'}
                    {inv.invoiceStatus === 'outstanding' && <span className="ml-2 text-xs font-semibold uppercase bg-red-200 text-red-900 px-1.5 py-0.5 rounded">Overdue</span>}
                    {inv.invoiceToken && (
                      <a
                        href={inv.segment === "PORT" ? `/portal-invoice/${inv.invoiceToken}` : `/invoice/${inv.invoiceToken}`}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-2 underline text-red-600 text-xs"
                      >View Invoice</a>
                    )}
                  </p>
                ))}
              </div>
            </div>
          </div>
        )}
        {readOnlyBanner ? (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
            data-testid="customer-readonly-pic-banner"
          >
            {readOnlyBanner}
          </div>
        ) : null}
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-4">
          <button
            type="button"
            onClick={() => setLocation("/customers")}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors self-start"
            aria-label="Back to customers"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex min-w-0 flex-1 flex-col gap-4 sm:flex-row sm:items-start">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[#1a2744] text-lg font-semibold text-white">
              {nameInitials}
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold tracking-tight">{customer.name}</h1>
                {typeof customer.relationshipType === "string" && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-900">{customer.relationshipType}</span>}
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">Active</span>
                {isTestCustomer && (
                  <span
                    className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700"
                    title="Internal test customer: skipped by automated reminders, Cleo signals and sales/revenue figures"
                  >
                    Test
                  </span>
                )}
                {isAdmin && (
                  <label className="ml-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Switch
                      checked={isTestCustomer}
                      disabled={setTestCustomer.isPending}
                      onCheckedChange={(checked) =>
                        setTestCustomer.mutate({ customerId: Number(customer.id), isTest: checked === true })
                      }
                      aria-label="Test customer"
                    />
                    Test customer
                  </label>
                )}
              </div>
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <span>
                  Customer no. <span className="font-semibold text-foreground">{customer.id}</span>
                </span>
                <button
                  type="button"
                  className="rounded p-1 transition-colors hover:bg-gray-100 hover:text-foreground"
                  aria-label={`Copy customer number ${customer.id}`}
                  title="Copy customer number"
                  onClick={async () => {
                    if (await copyTextToClipboard(String(customer.id))) {
                      toast.success(`Customer number ${customer.id} copied`);
                    } else {
                      toast.error("Could not copy the customer number");
                    }
                  }}
                >
                  <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                {customer.contact ? (
                  <span className="flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5" />
                    <a href={`tel:${customer.contact}`} className="text-foreground hover:underline">
                      {customer.contact}
                    </a>
                    {waHref ? (
                      <a
                        href={waHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-1 text-xs font-medium text-emerald-700 hover:underline"
                      >
                        WhatsApp
                      </a>
                    ) : null}
                  </span>
                ) : null}
                {customer.email ? (
                  <span className="flex items-center gap-1.5 min-w-0">
                    <Mail className="h-3.5 w-3.5 shrink-0" />
                    <a href={`mailto:${customer.email}`} className="truncate text-foreground hover:underline">
                      {customer.email}
                    </a>
                    {(customerEmailsData?.emails?.filter((e) => e.type === "ALTERNATIVE").length ?? 0) > 0 ? (
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        +{customerEmailsData!.emails.filter((e) => e.type === "ALTERNATIVE").length} alt
                      </span>
                    ) : null}
                  </span>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">
                Added on {formatDate(customer.createdAt)}
                {createdByUserName ? ` by ${createdByUserName}` : ""}
                {creationSource ? ` · Source: ${formatCustomerCreationSource(creationSource)}` : ""}
              </p>
              <div className="pt-3">
                <CustomerPhoneNumbersSection customerId={customer.id} canManage={canManageCustomer} />
              </div>
              {canReadCommercial && salesCardMetrics.data ? (
                <div className="grid grid-cols-2 gap-2 pt-1 sm:grid-cols-4">
                  <div className="rounded-md border border-[#1a2744]/10 bg-[#FAFAF7] px-3 py-2">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Last contact</p>
                    <p className="text-sm font-semibold text-[#1a2744]">
                      {salesCardMetrics.data.lastContactAt
                        ? formatDate(salesCardMetrics.data.lastContactAt)
                        : "—"}
                    </p>
                  </div>
                  <div className="rounded-md border border-[#1a2744]/10 bg-[#FAFAF7] px-3 py-2">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Total visits</p>
                    <p className="text-sm font-semibold text-[#1a2744]">{salesCardMetrics.data.totalVisits}</p>
                  </div>
                  <div className="rounded-md border border-[#1a2744]/10 bg-[#FAFAF7] px-3 py-2">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Proposals sent</p>
                    <p className="text-sm font-semibold text-[#1a2744]">{salesCardMetrics.data.proposalsSent}</p>
                  </div>
                  <div className="rounded-md border border-[#1a2744]/10 bg-[#FAFAF7] px-3 py-2">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Total spent</p>
                    <p className="text-sm font-semibold text-[#1a2744]">
                      {salesCardMetrics.data.purchaseCount > 0
                        ? `${salesCardMetrics.data.purchaseCount} purchase${salesCardMetrics.data.purchaseCount === 1 ? "" : "s"}`
                        : "—"}
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                {canCreateSvr ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const params = new URLSearchParams({
                    customerId: String(customer.id),
                    customerName: customer.name,
                    customerContact: customer.contact || "",
                    customerEmail: customer.email || "",
                    customerAddress: customer.address || "",
                  });
                  setLocation(`/site-visits/new?${params.toString()}`);
                }}
              >
                <CalendarDays className="h-3.5 w-3.5 mr-1" />
                New Site Visit
              </Button>
                ) : null}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const params = new URLSearchParams({
                    customerId: String(customer.id),
                    customerName: customer.name,
                    customerContact: customer.contact || "",
                    customerEmail: customer.email || "",
                    customerAddress: customer.address || "",
                  });
                  setLocation(`/repair-orders/new?${params.toString()}`);
                }}
              >
                <Wrench className="h-3.5 w-3.5 mr-1" />
                New RO
              </Button>
              {canManageCustomer && (
                <>
                  <Button variant="outline" size="sm" onClick={startEditCustomer}>
                    <Edit2 className="h-3.5 w-3.5 mr-1" />
                    Edit customer
                  </Button>
                  {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:border-red-300"
                    onClick={handleDeleteCustomer}
                    disabled={deleteCustomer.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Delete
                  </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Edit Customer Form */}
        {editingCustomer && (
          <Card className="border-blue-200 bg-blue-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Edit Customer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="editName">Full Name</Label>
                  <Input
                    id="editName"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="editContact">Contact Number</Label>
                  <Input
                    id="editContact"
                    value={editContact}
                    onChange={(e) => setEditContact(e.target.value)}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="editEmail">Email</Label>
                  <Input
                    id="editEmail"
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="editUnitNumber">Unit Number</Label>
                  <Input
                    id="editUnitNumber"
                    value={editUnitNumber}
                    onChange={(e) => setEditUnitNumber(e.target.value)}
                    placeholder="e.g. #12-34"
                    className="mt-1.5"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Address (search to auto-fill postal code)</Label>
                  <div className="mt-1.5">
                    <AddressSearchInput
                      value={editAddress || editPostalCode}
                      placeholder="Search by address or postal code..."
                      onAddressSelect={(address, postalCode) => {
                        setEditAddress(address);
                        setEditPostalCode(postalCode);
                      }}
                    />
                  </div>
                  {editPostalCode && (
                    <div className="mt-1.5 flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 text-sm">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Postal Code</span>
                      <span className="font-mono font-semibold text-foreground ml-auto">{editPostalCode}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveCustomer} disabled={updateCustomer.isPending}>
                  {updateCustomer.isPending ? "Saving..." : "Save Changes"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditingCustomer(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
              <User className="h-4 w-4" />
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {(customerEmailsData?.emails?.length || customer.email) ? (
                <div className="flex items-start gap-2 sm:col-span-2">
                  <Mail className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="min-w-0 space-y-2">
                    <p className="text-xs text-muted-foreground">Email addresses</p>
                    {(customerEmailsData?.emails?.length
                      ? customerEmailsData.emails
                      : customer.email
                        ? [{ id: 0, email: customer.email, type: "PRIMARY" as const, source: "OTHER" }]
                        : []
                    ).map((row) => (
                      <div key={`${row.type}-${row.id}-${row.email}`} className="min-w-0">
                        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          {row.type === "PRIMARY" ? "Primary" : "Alternative"}
                          {row.source === "CUSTOMER_APPROVAL" ? " · from approval" : ""}
                        </p>
                        <a href={`mailto:${row.email}`} className="font-medium break-all hover:underline">
                          {row.email}
                        </a>
                      </div>
                    ))}
                    <p className="text-[10px] text-muted-foreground">
                      Alternative emails from proposal approval are contact records only — not marketing consent.
                    </p>
                  </div>
                </div>
              ) : null}
              {customer.contact && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Contact</p>
                    <p className="font-medium">{customer.contact}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Primary address (from customer record) */}
            {((customer as any).unitNumber || (customer as any).postalCode || customer.address) && (
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground">Primary customer address</p>
                  <p className="font-medium">
                    {(customer as any).unitNumber ? `${(customer as any).unitNumber} ` : ""}
                    {customer.address || ""}
                    {(customer as any).postalCode && !customer.address?.includes((customer as any).postalCode)
                      ? ` (${(customer as any).postalCode})` : ""}
                  </p>
                </div>
              </div>
            )}

            <CustomerServiceSitesPanel
              customerId={customer.id}
              salespersonId={(customer as { salespersonId?: number | null }).salespersonId ?? null}
              actor={user ? { id: user.id, role: user.role } : null}
            />
          </CardContent>
        </Card>

        {/* Salesperson Assignment */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              Assigned Salesperson
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            {!editingSalesperson ? (
              <div className="flex items-center justify-between">
                <div>
                  {(() => {
                    const displayName = customer.salespersonName ||
                      (customer.salespersonId
                        ? salesStaff?.find((s) => s.id === customer.salespersonId)?.name
                        : null);
                    return displayName
                      ? <p className="font-medium">{displayName}</p>
                      : <p className="text-muted-foreground italic">No salesperson assigned</p>;
                  })()}
                </div>
                <div className="flex items-center gap-2">
                  {!customer.salespersonId && !isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      onClick={() => claimCustomer.mutate({ id: customer.id })}
                      disabled={claimCustomer.isPending}
                    >
                      Claim
                    </Button>
                  )}
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedSalespersonId(customer.salespersonId ?? null);
                        setEditingSalesperson(true);
                      }}
                    >
                      Reassign
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <select
                  value={selectedSalespersonId ?? ""}
                  onChange={(e) =>
                    setSelectedSalespersonId(e.target.value ? parseInt(e.target.value) : null)
                  }
                  className="flex-1 border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">— Unassigned —</option>
                  {salesStaff?.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={handleSalespersonSave}
                  disabled={updateSalesperson.isPending}
                >
                  Save
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditingSalesperson(false)}>
                  Cancel
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mb-6">
          <ClarityOnePortalToggle
            customerId={customer.id}
            enabled={Boolean(customer.customerPortalEnabled)}
            primaryContact={customer.contact}
            canViewAsCustomer={canStaffImpersonateClarityOneCustomer(user?.role)}
          />
          {(canManageCustomer || user?.role === "finance") && <PaymentWelcomeStatus customerId={customer.id} />}
        </div>

        <div className="mb-6">
          <KaleidescapeWalletStaffPanel
            customerId={customer.id}
            walletEnabled={Boolean((customer as { kaleidescapeWalletEnabled?: boolean }).kaleidescapeWalletEnabled)}
          />
        </div>

        {/* Tabbed section */}
        <Card>
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as CustomerCardTab)}
          >
            <CardHeader className="pb-0">
              <TabsList className="w-full justify-start flex-wrap rounded-none border-b bg-transparent p-0 h-auto gap-0">
                {canReadCommercial ? (
                  <TabsTrigger
                    value="timeline"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent px-4 py-2.5 text-sm font-medium"
                  >
                    <Clock className="h-3.5 w-3.5 mr-1.5" />
                    Timeline
                  </TabsTrigger>
                ) : null}
                {canReadFinance ? (
                <TabsTrigger
                  value="purchase"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent px-4 py-2.5 text-sm font-medium"
                >
                  <Package className="h-3.5 w-3.5 mr-1.5" />
                  Purchase History
                </TabsTrigger>
                ) : null}
                {canReadFinance ? (
                  <TabsTrigger
                    value="billing"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent px-4 py-2.5 text-sm font-medium"
                  >
                    <Receipt className="h-3.5 w-3.5 mr-1.5" />
                    Billing
                    {billingGroupsQ.data && billingGroupsQ.data.length > 0 ? (
                      <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-900">
                        {billingGroupsQ.data.length}
                      </span>
                    ) : null}
                  </TabsTrigger>
                ) : null}
                {canAccessInstallProjects ? (
                  <TabsTrigger
                    value="projects"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent px-4 py-2.5 text-sm font-medium"
                  >
                    <FolderOpen className="h-3.5 w-3.5 mr-1.5" />
                    Projects
                  </TabsTrigger>
                ) : null}
                <TabsTrigger
                  value="delivered"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent px-4 py-2.5 text-sm font-medium"
                >
                  <Package className="h-3.5 w-3.5 mr-1.5" />
                  Delivered Items
                </TabsTrigger>
                {canReadCommercial ? (
                  <TabsTrigger
                    value="proposals"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent px-4 py-2.5 text-sm font-medium"
                  >
                    <FileText className="h-3.5 w-3.5 mr-1.5" />
                    Proposals
                    {confirmedProposalCards.length > 0 ? (
                      <span className="ml-1.5 rounded-full bg-[#1a2744]/10 px-1.5 py-0.5 text-[10px] font-medium text-[#1a2744]">
                        {confirmedProposalCards.length}
                      </span>
                    ) : null}
                  </TabsTrigger>
                ) : null}
                <TabsTrigger
                  value="svr"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent px-4 py-2.5 text-sm font-medium"
                >
                  <MapPin className="h-3.5 w-3.5 mr-1.5" />
                  SVR
                </TabsTrigger>
                <TabsTrigger
                  value="media"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent px-4 py-2.5 text-sm font-medium"
                >
                  <FolderOpen className="h-3.5 w-3.5 mr-1.5" />
                  Media
                </TabsTrigger>
                <TabsTrigger
                  value="ro"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent px-4 py-2.5 text-sm font-medium"
                >
                  <Wrench className="h-3.5 w-3.5 mr-1.5" />
                  Repair Orders
                </TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent className="pt-4">
              {canReadCommercial ? (
                <TabsContent value="timeline" className="mt-0">
                  <div className="grid gap-6 lg:grid-cols-[1fr_300px] lg:items-start">
                    <div className="space-y-3">
                      <p className="text-xs text-muted-foreground">
                        <span className="font-semibold text-[#1a2744]">LINK A</span> (preview / deal view) does not increment customer public view count. Public customer links are Link B.
                      </p>
                      {canMutatePicOwnedFollowUp ? (
                        <div className="flex justify-end">
                          <Button size="sm" variant="secondary" onClick={() => setReminderOpen(true)}>
                            Set Reminder
                          </Button>
                        </div>
                      ) : null}
                      {salesTimeline.isLoading ? (
                        <p className="text-sm text-muted-foreground py-8">Loading timeline…</p>
                      ) : (
                        <CustomerCardTimeline milestones={(salesTimeline.data?.milestones ?? []) as any} />
                      )}
                    </div>
                    <div className="space-y-4">
                      <Card className="border-[#d7d7cc] bg-[#FAFAF7]" data-testid="customer-visits-card">
                        <CardHeader>
                          <CardTitle className="flex items-baseline justify-between gap-2 text-sm text-[#1a2744]">
                            <span>Visits</span>
                            {customerVisits.data ? (
                              <span className="text-xs font-normal text-muted-foreground">
                                {customerVisits.data.visitCount} visit
                                {customerVisits.data.visitCount === 1 ? "" : "s"}
                                {customerVisits.data.upcomingCount > 0
                                  ? ` · ${customerVisits.data.upcomingCount} upcoming`
                                  : ""}
                              </span>
                            ) : null}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {customerVisits.isLoading ? (
                            <p className="text-xs text-muted-foreground">Loading…</p>
                          ) : (customerVisits.data?.visits ?? []).length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                              No visits yet.{" "}
                              <a href="/sales/appointments" className="underline">
                                Book an appointment
                              </a>
                            </p>
                          ) : (
                            (customerVisits.data?.visits ?? []).slice(0, 10).map((v) => (
                              <div key={v.id} className="rounded border bg-white p-2 space-y-0.5">
                                <p className="text-xs font-medium text-[#1a2744]">
                                  {new Date(v.startsAt).toLocaleString("en-SG", {
                                    timeZone: "Asia/Singapore",
                                    dateStyle: "medium",
                                    timeStyle: "short",
                                  })}
                                  {!v.isPast ? (
                                    <span className="ml-1 rounded bg-amber-50 px-1 text-[10px] font-normal text-amber-800">
                                      upcoming
                                    </span>
                                  ) : null}
                                </p>
                                <p className="text-[11px] text-muted-foreground">
                                  {v.salespersonName || v.creatorName || "—"}
                                  {v.creatorName && v.salespersonName && v.creatorName !== v.salespersonName
                                    ? ` (created by ${v.creatorName})`
                                    : ""}
                                  {v.proposalNumber ? ` · Proposal ${v.proposalNumber}` : ""}
                                </p>
                                <p className="text-[11px] text-muted-foreground line-clamp-2">{v.title}</p>
                              </div>
                            ))
                          )}
                          {(customerVisits.data?.visits ?? []).length > 0 ? (
                            <a
                              href="/sales/appointments"
                              className="text-[11px] underline text-muted-foreground inline-block pt-1"
                            >
                              Team appointments →
                            </a>
                          ) : null}
                        </CardContent>
                      </Card>
                      <Card className="border-[#d7d7cc] bg-[#FAFAF7]">
                        <CardHeader>
                          <CardTitle className="text-sm text-[#1a2744]">Pending reminders</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {(reminderRows.data ?? []).filter((r: any) => r.status === "pending").length === 0 ? (
                            <p className="text-xs text-muted-foreground">No pending reminders.</p>
                          ) : (
                            (reminderRows.data ?? [])
                              .filter((r: any) => r.status === "pending")
                              .slice(0, 6)
                              .map((r: any) => (
                                <div key={r.id} className="rounded border bg-white p-2 space-y-1">
                                  <p className="text-xs font-medium text-[#1a2744]">
                                    {r.dueAt
                                      ? new Date(r.dueAt).toLocaleString("en-SG", { dateStyle: "medium", timeStyle: "short" })
                                      : `${r.reminderDate} (9:00 AM summary)`}
                                  </p>
                                  {r.note ? <p className="text-xs text-muted-foreground whitespace-pre-wrap">{r.note}</p> : null}
                                  {canMutatePicOwnedFollowUp ? (
                                  <div className="flex gap-1.5">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs"
                                      disabled={updateReminderStatus.isPending}
                                      onClick={() => updateReminderStatus.mutate({ reminderId: r.id, status: "done" })}
                                    >
                                      Done
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 text-xs text-destructive"
                                      disabled={updateReminderStatus.isPending}
                                      onClick={() => updateReminderStatus.mutate({ reminderId: r.id, status: "cancelled" })}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                  ) : null}
                                </div>
                              ))
                          )}
                        </CardContent>
                      </Card>
                      <CleoCustomerCardPanel
                        customerId={customerIdFromRoute}
                        proposalId={Number((proposalCards.data ?? [])[0]?.id ?? 0) || undefined}
                        customerContact={String(data?.customer?.contact ?? "") || undefined}
                        customerFirstName={firstName}
                        lastContactAt={salesCardMetrics.data?.lastContactAt}
                        autoStart={new URLSearchParams(search).get("cleoUpdate") === "1"}
                        canMutate={canMutatePicOwnedFollowUp}
                      />
                    </div>
                  </div>
                </TabsContent>
              ) : null}

              {canReadCommercial && canMutatePicOwnedFollowUp ? (
              <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Set follow-up reminder</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={reminderDate} onChange={(e) => setReminderDate(e.target.value)} />
                    </div>
                    <div>
                      <Label>Time (optional)</Label>
                      <Input type="time" value={reminderTime} onChange={(e) => setReminderTime(e.target.value)} />
                      <p className="text-xs text-muted-foreground mt-1">Leave blank for 9:00 AM daily summary reminder.</p>
                    </div>
                    <div>
                      <Label>Note</Label>
                      <textarea
                        value={reminderNote}
                        onChange={(e) => setReminderNote(e.target.value)}
                        className="mt-1 w-full min-h-[90px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setReminderOpen(false)}>
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        disabled={setCustomerReminder.isPending || !reminderDate}
                        onClick={() =>
                          setCustomerReminder.mutate({
                            customerId: customerIdFromRoute,
                            date: reminderDate,
                            time: reminderTime || undefined,
                            note: reminderNote || undefined,
                          })
                        }
                      >
                        Save reminder
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              ) : null}

              {canReadFinance ? (
              <>
              <TabsContent value="billing" className="mt-0 space-y-4">
                <CustomerBillingGroupPanel
                  groups={(billingGroupsQ.data as any) ?? []}
                  onRefresh={() => void billingGroupsQ.refetch()}
                  canGenerate={!Boolean(access?.isReadOnlyBecauseOtherPic)}
                />
              </TabsContent>

              <TabsContent value="purchase" className="mt-0 space-y-6">
                {canViewWarranty ? (
                  <Card className="border-[#d7d7cc] bg-[#FAFAF7]" data-testid="customer-warranties-card">
                    <CardHeader>
                      <CardTitle className="text-base text-[#1a2744]">Warranties</CardTitle>
                      <p className="text-xs text-muted-foreground">
                        Current warranty records for this customer.
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {customerWarranties.isLoading ? (
                        <p className="text-sm text-muted-foreground">Loading warranties…</p>
                      ) : customerWarranties.error ? (
                        <div className="flex flex-wrap items-center gap-2 rounded border border-amber-200 bg-amber-50 p-3">
                          <p className="mr-auto text-sm text-amber-900">Warranties are temporarily unavailable.</p>
                          <Button size="sm" variant="outline" onClick={() => void customerWarranties.refetch()}>
                            Retry
                          </Button>
                        </div>
                      ) : (customerWarranties.data ?? []).length === 0 ? (
                        <p className="text-sm text-muted-foreground">No warranty records yet.</p>
                      ) : (
                        (customerWarranties.data ?? []).map((warranty) => {
                          const isLive = warranty.status === "issued";
                          return (
                            <div
                              key={`customer-warranty-${warranty.id}`}
                              className={`flex flex-wrap items-center justify-between gap-3 rounded border bg-white px-3 py-3 ${
                                isLive ? "border-emerald-300" : ""
                              }`}
                            >
                              <div className="min-w-0">
                                <p className={`text-sm font-semibold ${isLive ? "text-emerald-700" : "text-[#1a2744]"}`}>
                                  {warrantyCardStaffStatusLabel(warranty.status)}
                                </p>
                                <p className="truncate text-xs text-muted-foreground">
                                  {warranty.invoiceNumberSnapshot || warranty.proposalRefSnapshot || `Warranty #${warranty.id}`}
                                </p>
                                {isLive && warranty.issuedAt ? (
                                  <p className="text-xs text-muted-foreground">
                                    Issued {new Date(warranty.issuedAt).toLocaleDateString("en-SG")}
                                  </p>
                                ) : null}
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className={`h-8 text-xs ${isLive ? "border-emerald-300 text-emerald-700" : ""}`}
                                onClick={() => setLocation(`/warranties/${warranty.id}`)}
                              >
                                <Shield className="mr-1 h-3.5 w-3.5" />
                                {isLive ? "Open Live Warranty" : "Open Warranty"}
                              </Button>
                            </div>
                          );
                        })
                      )}
                    </CardContent>
                  </Card>
                ) : null}
                {canReadFinance && (legacyInvoices.data ?? []).some((r: any) => r.status === "unpaid") ? (
                  <Card className="border-[#d7d7cc] bg-[#FAFAF7]">
                    <CardHeader>
                      <CardTitle className="text-base text-[#1a2744]">Legacy invoices (projected)</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {(legacyInvoices.data ?? [])
                        .filter((r: any) => r.status === "unpaid")
                        .slice(0, 8)
                        .map((r: any) => (
                          <div key={`legacy-open-${r.id}`} className="flex items-center justify-between rounded border bg-white px-3 py-2 text-xs">
                            <div className="space-y-0.5">
                              <p className="font-mono text-[#1a2744]">{r.invoiceNumber}</p>
                              <p className="text-muted-foreground">
                                {r.invoiceDate ? new Date(r.invoiceDate).toLocaleDateString("en-SG") : "—"} · SGD {Number(r.amount).toLocaleString("en-SG", { maximumFractionDigits: 2 })}
                              </p>
                            </div>
                            {r.uploadedPdfUrl ? <a className="underline" target="_blank" rel="noreferrer" href={r.uploadedPdfUrl}>View PDF</a> : null}
                          </div>
                        ))}
                    </CardContent>
                  </Card>
                ) : null}
                {canReadCommercial && (paidRetailPurchases.data?.length ?? 0) > 0 ? (
                  <Card className="border-[#d7d7cc] bg-[#FAFAF7]">
                    <CardHeader>
                      <CardTitle className="text-base text-[#1a2744]">Retail purchases (paid)</CardTitle>
                      <p className="text-xs text-muted-foreground">Won deals with a paid approval invoice move here from the Proposals tab.</p>
                    </CardHeader>
                    <CardContent className="overflow-x-auto -mx-1">
                      <table className="w-full text-sm min-w-[520px]">
                        <thead>
                          <tr className="border-b text-left text-xs text-muted-foreground">
                            <th className="py-2 pr-2">Ref</th>
                            <th className="py-2 pr-2">Amount</th>
                            <th className="py-2 pr-2">Paid</th>
                            <th className="py-2 pr-2">Sales</th>
                            <th className="py-2" />
                          </tr>
                        </thead>
                        <tbody>
                          {(paidRetailPurchases.data ?? []).map((r) => (
                            <tr key={r.proposalId} className="border-b border-[#1a2744]/8">
                              <td className="py-2 pr-2 font-mono text-[#1a2744]">{r.proposalRef}</td>
                              <td className="py-2 pr-2 tabular-nums">
                                {Number(r.amount).toLocaleString("en-SG", { maximumFractionDigits: 2 })}
                              </td>
                              <td className="py-2 pr-2 text-xs text-muted-foreground">
                                {r.paidAt
                                  ? new Date(r.paidAt).toLocaleString("en-SG", { dateStyle: "medium", timeStyle: "short" })
                                  : "—"}
                              </td>
                              <td className="py-2 pr-2 text-xs">{r.salesperson ?? "—"}</td>
                              <td className="py-2 pl-1">
                                {r.invoiceUrl ? (
                                  <a
                                    className="text-sm font-medium text-[#1a2744] underline"
                                    href={r.invoiceUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Invoice
                                  </a>
                                ) : (
                                  "—"
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                ) : null}
                {canReadFinance && (legacyInvoices.data ?? []).some((r: any) => r.status === "paid") ? (
                  <Card className="border-[#d7d7cc] bg-[#FAFAF7]">
                    <CardHeader>
                      <CardTitle className="text-base text-[#1a2744]">Legacy invoices (paid)</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {(legacyInvoices.data ?? [])
                        .filter((r: any) => r.status === "paid")
                        .slice(0, 8)
                        .map((r: any) => (
                          <div key={`legacy-paid-${r.id}`} className="flex items-center justify-between rounded border bg-white px-3 py-2 text-xs">
                            <div>
                              <p className="font-mono text-[#1a2744]">{r.invoiceNumber}</p>
                              <p className="text-muted-foreground">
                                Paid {r.paidAt ? new Date(r.paidAt).toLocaleDateString("en-SG") : "—"} · SGD {Number(r.amount).toLocaleString("en-SG", { maximumFractionDigits: 2 })}
                              </p>
                            </div>
                            {r.uploadedPdfUrl ? <a className="underline" target="_blank" rel="noreferrer" href={r.uploadedPdfUrl}>View PDF</a> : null}
                          </div>
                        ))}
                    </CardContent>
                  </Card>
                ) : null}
                {(portalInvoices.data ?? []).length > 0 ? (
                  <Card className="border-[#d7d7cc] bg-[#FAFAF7]">
                    <CardHeader>
                      <CardTitle className="text-base text-[#1a2744]">Portal invoices</CardTitle>
                      <p className="text-xs text-muted-foreground">Payment schedules and collection progress for finance invoices.</p>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {(portalInvoices.data ?? []).map((invoice) => {
                        const collected = invoice.amount > 0 ? Math.min(100, Math.round((invoice.paidAmount / invoice.amount) * 100)) : 0;
                        return (
                          <div
                            key={`portal-inv-${invoice.id}`}
                            className="rounded border bg-white px-3 py-3 space-y-2"
                          >
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div>
                                <p className="font-mono text-sm text-[#1a2744]">{invoice.invoiceNumber}</p>
                                <p className="text-xs text-muted-foreground">
                                  {invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString("en-SG") : "—"} · SGD{" "}
                                  {invoice.amount.toLocaleString("en-SG", { maximumFractionDigits: 2 })} · {invoice.invoiceStatus}
                                </p>
                              </div>
                              <div className="flex flex-wrap items-center justify-end gap-2">
                                {invoice.invoiceToken ? (
                                  <a className="text-xs underline" href={`/portal-invoice/${invoice.invoiceToken}`} target="_blank" rel="noreferrer">
                                    View invoice
                                  </a>
                                ) : null}
                                {(() => {
                                  const action = portalWarrantyActions.data?.[invoice.id]?.action;
                                  if (!action || !canViewWarranty) return null;
                                  if (action.kind === "view") {
                                    const status = portalWarrantyActions.data?.[invoice.id]?.card?.status;
                                    const label = warrantyCardStaffStatusLabel(status);
                                    return (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className={`h-7 text-xs ${status === "issued" ? "border-emerald-300 text-emerald-700" : ""}`}
                                        onClick={() => setLocation(`/warranties/${action.warrantyCardId}`)}
                                      >
                                        <Shield className="mr-1 h-3 w-3" />
                                        {label}
                                      </Button>
                                    );
                                  }
                                  if (!canEditWarranty) return null;
                                  if (action.kind === "continue") {
                                    return (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-xs"
                                        onClick={() => setLocation(`/warranties/${action.warrantyCardId}`)}
                                      >
                                        <Shield className="mr-1 h-3 w-3" />
                                        CONTINUE WARRANTY DRAFT
                                      </Button>
                                    );
                                  }
                                  if (action.kind === "disabled") {
                                    return (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-xs"
                                        disabled
                                        title={action.message}
                                      >
                                        <Shield className="mr-1 h-3 w-3" />
                                        CREATE WARRANTY DRAFT
                                      </Button>
                                    );
                                  }
                                  return (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs"
                                      disabled={createPortalWarranty.isPending}
                                      onClick={() =>
                                        createPortalWarranty.mutate(
                                          {
                                            sourceInvoiceType: "portal",
                                            sourceInvoiceId: invoice.id,
                                          },
                                          {
                                            onSuccess: (result) => {
                                              toast.success("Warranty draft created");
                                              setLocation(`/warranties/${result.card.id}`);
                                            },
                                          },
                                        )
                                      }
                                    >
                                      <Shield className="mr-1 h-3 w-3" />
                                      CREATE WARRANTY DRAFT
                                    </Button>
                                  );
                                })()}
                              </div>
                            </div>
                            <div className="h-2 rounded-full bg-[#1a2744]/10 overflow-hidden">
                              <div className="h-full bg-[#1a2744] transition-all" style={{ width: `${collected}%` }} />
                            </div>
                            <div className="space-y-1">
                              {invoice.schedules.map((schedule, index) => (
                                <div key={`${invoice.id}-${schedule.id ?? index}`} className="flex flex-wrap items-center justify-between gap-2 text-xs">
                                  <div>
                                    <p className="text-[#1a2744]">
                                      {schedule.percentage != null ? `${schedule.percentage}% ` : ""}
                                      {schedule.label}
                                    </p>
                                    <p className="text-muted-foreground">
                                      SGD {schedule.amount.toLocaleString("en-SG", { maximumFractionDigits: 2 })}
                                      {schedule.expectedDate ? ` · due ${schedule.expectedDate}` : ""}
                                    </p>
                                  </div>
                                  <span
                                    className={
                                      schedule.status === "paid"
                                        ? "text-green-700"
                                        : schedule.status === "overdue"
                                          ? "text-red-700"
                                          : "text-muted-foreground"
                                    }
                                  >
                                    {schedule.status === "paid"
                                      ? "Paid"
                                      : schedule.status === "partially_paid"
                                        ? `Partially paid (SGD ${schedule.paidAmount.toLocaleString("en-SG", { maximumFractionDigits: 2 })})`
                                        : schedule.status === "overdue"
                                          ? "Overdue"
                                          : "Outstanding"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                ) : null}
                <PurchaseHistoryTab customerId={parseInt(id ?? "0")} />
              </TabsContent>
              </>
              ) : null}

              {canReadCommercial ? (
              <TabsContent value="proposals" className="mt-0 space-y-4">
                <Card className="border-[#d7d7cc] bg-[#FAFAF7]">
                  <CardHeader>
                    <CardTitle className="text-base text-[#1a2744]">Proposals</CardTitle>
                    <p className="text-xs text-muted-foreground">
                      Latest version of each proposal only. Confirmed proposals have received their first required payment.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="inline-flex rounded-md border border-[#1a2744]/15 bg-white p-1" role="tablist" aria-label="Proposal confirmation status">
                      <Button
                        type="button"
                        size="sm"
                        variant={proposalConfirmationTab === "confirmed" ? "default" : "ghost"}
                        className={proposalConfirmationTab === "confirmed" ? "h-8 bg-[#1a2744] text-white" : "h-8"}
                        onClick={() => setProposalConfirmationTab("confirmed")}
                      >
                        Confirmed ({confirmedProposalCards.length})
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={proposalConfirmationTab === "unconfirmed" ? "default" : "ghost"}
                        className={proposalConfirmationTab === "unconfirmed" ? "h-8 bg-[#1a2744] text-white" : "h-8"}
                        onClick={() => setProposalConfirmationTab("unconfirmed")}
                      >
                        Unconfirmed ({unconfirmedProposalCards.length})
                      </Button>
                    </div>
                    {visibleProposalCards.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No {proposalConfirmationTab} proposals for this customer.
                      </p>
                    ) : (
                      <div className="overflow-x-auto -mx-1">
                        <table className="w-full text-sm min-w-[720px]">
                          <thead>
                            <tr className="border-b text-left text-xs text-muted-foreground">
                              <th className="py-2 pr-2">Ref</th>
                              <th className="py-2 pr-2">Date</th>
                              <th className="py-2 pr-2">Value (SGD)</th>
                              <th className="py-2 pr-2">Status</th>
                              <th className="py-2 pr-2">Temp</th>
                              <th className="py-2 pr-2">Last activity</th>
                              <th className="py-2 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleProposalCards.map((p: any) => {
                              const workspacePath = String(p.workspacePath ?? "").trim();
                              const publicProposalPath = String(p.publicProposalPath ?? "").trim();
                              const openWorkspace = () => {
                                if (!workspacePath) {
                                  toast.error("Proposal workspace unavailable");
                                  return;
                                }
                                navigate(workspacePath);
                              };
                              return (
                              <tr key={p.id} className="border-b border-[#1a2744]/8">
                                <td className="py-2 pr-2 font-medium text-[#1a2744] whitespace-nowrap">
                                  {p.proposalName ? <div>{p.proposalName}</div> : null}
                                  <button
                                    type="button"
                                    onClick={openWorkspace}
                                    className={
                                      p.proposalName
                                        ? "text-[11px] font-normal text-[#1a2744] font-mono text-left hover:underline underline-offset-2"
                                        : "font-mono text-left hover:underline underline-offset-2"
                                    }
                                  >
                                    {p.proposalRef}
                                  </button>
                                  {p.isManualUpload ? (
                                    <div className="text-[11px] font-normal text-muted-foreground">Manual PDF upload</div>
                                  ) : null}
                                </td>
                                <td className="py-2 pr-2 text-xs whitespace-nowrap">
                                  {new Date(p.createdAt).toLocaleDateString("en-SG")}
                                </td>
                                <td className="py-2 pr-2 text-xs">
                                  {Number(p.totalAmount ?? 0).toLocaleString("en-SG", { maximumFractionDigits: 2 })}
                                </td>
                                <td className="py-2 pr-2 text-xs uppercase">
                                  {p.confirmationGroup === "confirmed"
                                    ? "Confirmed"
                                    : String(p.displayStage ?? p.effectiveStage ?? p.status)}
                                </td>
                                <td className="py-2 pr-2 text-xs">{String(p.dealTemperature ?? "—")}</td>
                                <td className="py-2 pr-2 text-xs text-muted-foreground">
                                  {p.lastActivityAt
                                    ? new Date(p.lastActivityAt).toLocaleString("en-SG", {
                                        day: "numeric",
                                        month: "short",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })
                                    : "—"}
                                </td>
                                <td className="py-2 pl-1 text-right">
                                  <div className="flex flex-wrap justify-end gap-1.5">
                                    <Button
                                      size="sm"
                                      variant="default"
                                      className="h-8 bg-[#1a2744] text-white hover:bg-[#1a2744]/90"
                                      onClick={openWorkspace}
                                    >
                                      Open Workspace
                                    </Button>
                                    {publicProposalPath ? (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8 border-[#1a2744]/20"
                                        onClick={() => navigate(publicProposalPath)}
                                      >
                                        Public Link
                                      </Button>
                                    ) : null}
                                    {p.permissions?.canCreateVo &&
                                    (shouldUseVariationOrderWorkflow(p) ||
                                      p.permissions?.commercialHeadLocked) ? (
                                      <CustomerDetailCreateVoButton
                                        proposalId={
                                          Number(p.permissions?.commercialHeadProposalId ?? 0) > 0
                                            ? Number(p.permissions.commercialHeadProposalId)
                                            : Number(p.id)
                                        }
                                        fromPath={pathname}
                                      />
                                    ) : p.permissions?.canRevise && canCreateProposalRevision(p) ? (
                                      <Button
                                        size="sm"
                                        variant="default"
                                        className="h-8 bg-[#1a2744] text-white hover:bg-[#1a2744]/90"
                                        onClick={() =>
                                          navigate(`/sales/proposals/${p.id}/revise`, {
                                            state: { from: pathname },
                                          })
                                        }
                                      >
                                        Revise
                                      </Button>
                                    ) : null}
                                    {p.isManualUpload ? (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8"
                                        disabled={!p.hasUploadedOriginalPdf || openManualProposalPdf.isPending}
                                        onClick={() => openManualProposalPdf.mutate({ proposalId: p.id })}
                                      >
                                        PDF
                                      </Button>
                                    ) : null}
                                    {p.canRegenerateImported ? (
                                      <RegenerateProposalButton
                                        proposalId={p.id}
                                        eligible={Boolean(p.canRegenerateImported)}
                                        className="h-8"
                                        onCompleted={async () => {
                                          await proposalCards.refetch();
                                        }}
                                      />
                                    ) : null}
                                    {p.permissions?.canCancel ? (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-8 border-destructive/50 text-destructive hover:bg-destructive/10"
                                      onClick={openWorkspace}
                                    >
                                      Cancel in workspace
                                    </Button>
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                            );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              ) : null}

              {canAccessInstallProjects ? (
                <TabsContent value="projects" className="mt-0 space-y-4">
                  <CustomerProjectsSection
                    customerId={parseInt(id ?? "0")}
                    canMutate={canMutatePicOwnedFollowUp}
                  />
                </TabsContent>
              ) : null}

              <TabsContent value="delivered" className="mt-0 space-y-4">
                <CustomerDeliveredItemsSection customerId={parseInt(id ?? "0")} />
              </TabsContent>

              {/* SVR Tab */}
              <TabsContent value="svr" className="mt-0">
                <ServiceVisitsSection
                  customerId={parseInt(id ?? "0")}
                  onNewVisit={() => setLocation(`/site-visits/new?customerId=${id ?? ""}`)}
                  onViewVisit={(visitId) => navigate(`/site-visits/id/${visitId}`, { state: { from: pathname } })}
                />
              </TabsContent>

              <TabsContent value="media" className="mt-0">
                <CustomerMediaSection
                  customerId={parseInt(id ?? "0")}
                  enabled={activeTab === "media"}
                />
              </TabsContent>

              {/* RO Tab */}
              <TabsContent value="ro" className="mt-0">
                {jobs.length === 0 ? (
                  <div className="text-center py-10">
                    <Wrench className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No repair orders yet</p>
                    <button
                      onClick={() => {
                        const params = new URLSearchParams({
                          customerId: String(customer.id),
                          customerName: customer.name,
                          customerContact: customer.contact || "",
                          customerEmail: customer.email || "",
                          customerAddress: customer.address || "",
                        });
                        setLocation(`/repair-orders/new?${params.toString()}`);
                      }}
                      className="text-xs text-blue-600 hover:underline mt-1"
                    >
                      Create first repair order →
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {jobs.map((job) => {
                      const isExpanded = expandedJobId === job.id;
                      const isInternalExpanded = expandedInternalIds.has(job.id);
                      const hasCustomerReport = !!(job.serviceReport || job.diagnosedFault);
                      const hasInternalNotes = !!(job.serviceReportRaw || job.diagnosedFaultRaw);
                      return (
                        <div key={job.id} className="border rounded-md overflow-hidden">
                          <div
                            className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => toggleJob(job.id)}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-mono text-sm font-medium">{job.jobNumber}</p>
                                {(job.productBrand || job.productModel) && (
                                  <span className="text-xs text-muted-foreground bg-gray-100 px-1.5 py-0.5 rounded">
                                    {[job.productBrand, job.productModel].filter(Boolean).join(" ")}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {formatDate(job.createdAt)} · {job.faultReported.slice(0, 60)}{job.faultReported.length > 60 ? "..." : ""}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 ml-2 shrink-0">
                              <StatusBadge status={job.status} />
                              {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="border-t px-3 pb-3 pt-2 space-y-3 bg-gray-50/50">
                              {hasCustomerReport ? (
                                <div>
                                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Repair Order</p>
                                  {job.diagnosedFault && <p className="text-sm mb-1"><span className="font-medium">Diagnosis:</span> {job.diagnosedFault}</p>}
                                  {job.serviceReport && <p className="text-sm whitespace-pre-wrap">{job.serviceReport}</p>}
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground italic">No repair order written yet</p>
                              )}
                              {hasInternalNotes && (
                                <div>
                                  <button
                                    onClick={(e) => toggleInternal(job.id, e)}
                                    className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-amber-700 hover:text-amber-900 transition-colors"
                                  >
                                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isInternalExpanded ? "" : "-rotate-90"}`} />
                                    Internal Notes
                                  </button>
                                  {isInternalExpanded && (
                                    <div className="mt-1.5 p-2 bg-amber-50 border border-amber-200 rounded text-sm space-y-1">
                                      {job.diagnosedFaultRaw && <p><span className="font-medium">Raw Diagnosis:</span> {job.diagnosedFaultRaw}</p>}
                                      {job.serviceReportRaw && <p className="whitespace-pre-wrap">{job.serviceReportRaw}</p>}
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className="flex items-center gap-2 flex-wrap pt-1">
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate(`/repair-orders/id/${job.id}`, { state: { from: pathname } }); }}
                                  className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded px-2 py-1 transition-colors"
                                >
                                  Open full job record →
                                </button>
                                {(user?.role === "finance" || user?.role === "admin") &&
                                  job.status === "awaiting_payment" &&
                                  !(job as any).roInvoiceNumber && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); navigate(`/repair-orders/id/${job.id}`, { state: { from: pathname } }); }}
                                    className="flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-800 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded px-2 py-1 transition-colors"
                                  >
                                    <FileText className="h-3 w-3" />
                                    Raise Invoice
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>

        {/* System Information */}
      </div>
    </DashboardLayout>
  );
}

const STATUS_LABELS: Record<string, string> = {
  scheduled: "Scheduled",
  pending_review: "Pending Review",
  closed: "Closed",
  report_sent: "Closed (Report Sent)",
  waived: "Waived",
};

const STATUS_COLORS: Record<string, string> = {
  scheduled: "bg-blue-100 text-blue-800",
  pending_review: "bg-amber-100 text-amber-800",
  closed: "bg-gray-100 text-gray-700",
  report_sent: "bg-gray-100 text-gray-700",
  waived: "bg-purple-100 text-purple-800",
};

function ServiceVisitsSection({
  customerId,
  onNewVisit,
  onViewVisit,
}: {
  customerId: number;
  onNewVisit: () => void;
  onViewVisit: (visitId: number) => void;
}) {
  const { data: visits = [] } = trpc.siteVisits.byCustomer.useQuery({ customerId });
  const [expanded, setExpanded] = useState(false);
  const displayVisits = expanded ? visits : visits.slice(0, 5);

  // Group by year
  const byYear: Record<number, typeof visits> = {};
  for (const v of visits) {
    const year = new Date(v.visitDate).getFullYear();
    if (!byYear[year]) byYear[year] = [];
    byYear[year].push(v);
  }
  const years = Object.keys(byYear).map(Number).sort((a, b) => b - a);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center justify-between">
          <span className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Service Visits ({visits.length})
          </span>
          <button
            onClick={onNewVisit}
            className="flex items-center gap-1 text-xs font-normal text-black hover:text-gray-600 normal-case"
          >
            <Plus className="h-3.5 w-3.5" />
            New Visit
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {visits.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground">No service visits yet</p>
            <button onClick={onNewVisit} className="text-xs text-blue-600 hover:underline mt-1">
              Schedule the first visit →
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {years.map((year) => (
              <div key={year}>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{year}</p>
                <div className="space-y-1.5">
                  {byYear[year].map((visit) => (
                    <div
                      key={visit.id}
                      className="flex items-center justify-between p-2.5 border rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => onViewVisit(visit.id)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="font-mono text-xs text-muted-foreground shrink-0">{visit.visitNumber}</span>
                        <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium shrink-0 ${STATUS_COLORS[visit.status] ?? "bg-gray-100"}`}>
                          {STATUS_LABELS[visit.status] ?? visit.status}
                        </span>
                        {visit.complaintDescription && (
                          <span className="text-xs text-muted-foreground truncate">{visit.complaintDescription}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0 ml-2">
                        <span className="flex items-center gap-1">
                          <CalendarDays className="h-3 w-3" />
                          {formatDate(visit.visitDate)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
